import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import useScrollAnimation from './hooks/useScrollAnimation';
import './blog.css';
import './animations.css';

// Format date helper
function formatDate(date) {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (typeof date === 'string') return date;
  return date.toString();
}

// Dummy blog data for immediate visual testing
const dummyBlogs = [
  {
    id: 'minimalist-design',
    title: "The Art of Minimalist Interior Design",
    summary: "Explore how less can truly be more when it comes to creating serene, functional spaces that breathe with intention and purpose.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Philosophy",
    author: "Sarah Mitchell",
    date: "2025-01-15",
    readTime: "5 min read"
  },
  {
    id: 'sustainable-materials',
    title: "Sustainable Materials in Modern Architecture",
    summary: "How we're incorporating eco-friendly materials and practices into contemporary design without compromising on aesthetics or functionality.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Sustainability",
    author: "David Chen",
    date: "2025-01-10",
    readTime: "7 min read"
  },
  {
    id: 'commercial-productivity',
    title: "Commercial Spaces That Inspire Productivity",
    summary: "Behind the scenes of our recent commercial project and how thoughtful design can boost workplace performance and employee satisfaction.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1200&q=80",
    category: "Commercial Design",
    author: "Emma Rodriguez",
    date: "2025-01-05",
    readTime: "6 min read"
  },
  {
    id: 'color-psychology',
    title: "Color Psychology in Residential Interiors",
    summary: "Understanding how different colors affect mood and behavior, and how we use this knowledge to create homes that truly feel like sanctuaries.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Lisa Thompson",
    date: "2024-12-28",
    readTime: "4 min read"
  },
  {
    id: 'smart-home-future',
    title: "The Future of Smart Home Integration",
    summary: "How we're seamlessly incorporating smart technology into our designs without compromising aesthetic appeal or user experience.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Technology",
    author: "Michael Park",
    date: "2024-12-20",
    readTime: "5 min read"
  },
  {
    id: 'lighting-design',
    title: "Lighting Design: Sculpting Spaces with Light",
    summary: "The art and science of lighting design, and how the right illumination can completely transform the feel and function of any space.",
    image: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Anna Kowalski",
    date: "2024-12-15",
    readTime: "6 min read"
  },
  {
    id: 'biophilic-design',
    title: "Biophilic Design: Bringing Nature Indoors",
    summary: "How nature-inspired materials, light, and greenery improve well-being and comfort in modern interiors.",
    image: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?auto=format&fit=crop&w=1200&q=80",
    category: "Design Philosophy",
    author: "Nina Patel",
    date: "2025-01-20",
    readTime: "5 min read"
  }
];

// Extract unique categories from dummy data
const dummyCategories = ['All', ...new Set(dummyBlogs.map(blog => blog.category))];

// Blog card component
function BlogCard({ blog, onClick }) {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);
  
  return (
    <div className="btw-blog-card" ref={cardRef} onClick={onClick}>
      <div className={`btw-blog-image-wrapper fade-in-scale ${cardVisible ? 'visible' : ''}`}>
        <img src={blog.image} alt={blog.title} className="btw-blog-image" />
        <div className="btw-blog-category">[{blog.category.toUpperCase()}]</div>
      </div>
      <div className={`btw-blog-content fade-in-up ${cardVisible ? 'visible' : ''}`}>
        <h3 className="btw-blog-title">{blog.title}</h3>
        <p className="btw-blog-summary">{blog.summary}</p>
        <div className="btw-blog-meta">
          <span className="btw-blog-author">By {blog.author}</span>
          <span className="btw-blog-dot">•</span>
          <span className="btw-blog-date">{formatDate(blog.date)}</span>
          <span className="btw-blog-dot">•</span>
          <span className="btw-blog-read-time">{blog.readTime}</span>
        </div>
      </div>
    </div>
  );
}

export default function BlogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [blogs, setBlogs] = useState([]); // full list for current category
  const [visibleBlogs, setVisibleBlogs] = useState([]); // paginated slice
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [heroRef, heroVisible] = useScrollAnimation(0.3);

  // Read category from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [location.search]);

  // Set dummy data immediately for visual testing with simple pagination (6 per load)
  useEffect(() => {
    setLoading(true);
    
    // Simulate loading delay
    setTimeout(() => {
      setCategories(dummyCategories);
      
      // Filter blogs by category
      const filteredBlogs = selectedCategory === 'All' 
        ? dummyBlogs 
        : dummyBlogs.filter(blog => blog.category === selectedCategory);
      
      setBlogs(filteredBlogs);
      // Initialize visible slice (6 items) and hasMore flag
      const initial = filteredBlogs.slice(0, 6);
      setVisibleBlogs(initial);
      setHasMore(filteredBlogs.length > initial.length);
      setLoading(false);
    }, 500);
  }, [selectedCategory]);

  // Load more blogs - disabled for dummy data
  const loadMoreBlogs = async () => {
    if (loading) return;
    const next = blogs.slice(visibleBlogs.length, visibleBlogs.length + 6);
    const updated = [...visibleBlogs, ...next];
    setVisibleBlogs(updated);
    setHasMore(updated.length < blogs.length);
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setBlogs([]);
    
    if (category === 'All') {
      navigate('/blog', { replace: true });
    } else {
      navigate(`/blog?category=${encodeURIComponent(category)}`, { replace: true });
    }
  };

  return (
    <main style={{minHeight: '100vh'}}>
      {/* Hero Section */}
      <section className="btw-blog-hero" ref={heroRef}>
        <div className="btw-blog-hero-content">
          <div className={`btw-blog-hero-subtitle subtitle-animate ${heroVisible ? 'visible' : ''}`}>
            [DESIGN INSIGHTS]
          </div>
          <h1 className={`btw-blog-hero-title hero-title-animate ${heroVisible ? 'visible' : ''}`} style={{ color: '#baa170' }}>
            STORIES & INSPIRATION<br />FROM OUR STUDIO
          </h1>
          <p className={`btw-blog-hero-desc description-animate ${heroVisible ? 'visible' : ''}`} style={{ color: '#181618' }}>
            Explore the latest trends, behind-the-scenes insights, and design philosophy<br />
            that shapes every project we create at Between the Walls.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="btw-blog-categories-section">
        <div className="btw-blog-categories">
          {categories.map(category => (
            <button
              key={category}
              className={`btw-blog-category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategorySelect(category)}
            >
              <span>{category}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="btw-blog-grid-section">
        <div className="btw-blog-grid">
          {visibleBlogs.map((blog) => (
            <BlogCard
              key={blog.id}
              blog={blog}
              onClick={() => navigate(`/blog/${blog.id}`)}
            />
          ))}
        </div>

        {loading && (
          <div className="btw-blog-loading">
            <div className="btw-blog-loading-text">Loading insights...</div>
          </div>
        )}

        {!loading && hasMore && (
          <div className="btw-blog-load-more">
            <button className="btw-cta" onClick={loadMoreBlogs}>
              <span>Load More Articles</span>
            </button>
          </div>
        )}

        {!loading && blogs.length === 0 && (
          <div className="btw-blog-empty">
            <h3>No articles found</h3>
            <p>We're working on creating amazing content for this category.</p>
            <Link to="/blog">
              <button className="btw-cta">View All Articles</button>
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}